import { Model } from 'app/model/backendmodel';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { WaitActivity } from 'app/model/pipeline/wait.activity';
import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { ActivityAsyncAdapter } from 'app/model/adapters/activities/activity.adapter';

import { ValueAdapter } from 'app/model/adapters/value.adapter';
import _ from 'lodash';
import { IWaitActivityContext } from 'app/interfaces/copilot.pipeline.action.types';
import { isLogicAppExpression } from 'app/shared/utils/expression.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(WaitActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        type: '*',
        description: '*',
        dependsOn: '*',
        policy: '*',
        userProperties: '*',
        typeProperties: {
            waitTimeInSeconds: '*'
        }
    }
})
export class WaitActivityAdapter extends ActivityAsyncAdapter<WaitActivity, Model.WaitActivity, IWaitActivityContext> {

    public model(): IModel {
        return Metamodel.modelFor(WaitActivity);
    }

    public override async toFabricCopilotContext(activity: WaitActivity): Promise<IWaitActivityContext> {
        if (activity) {
            const activityContext: IWaitActivityContext = await super.toFabricCopilotContext(activity);
            activityContext.WaitSeconds = ValueAdapter.toResource(activity, WaitActivity.WAIT_TIME, true)?.toString();
            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: IWaitActivityContext,
        model?: WaitActivity
    ): Promise<WaitActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            if (context.WaitSeconds != null) { 
                let waitInSecondsInt: string | number = context.WaitSeconds;
                if (!isLogicAppExpression(waitInSecondsInt)) { 
                    try { 
                        waitInSecondsInt = parseInt(waitInSecondsInt, 10); 
                    } catch (e) { 
                        // expression value is received, do nothing 
                    }
                } 
                ValueAdapter.toModel(model, WaitActivity.WAIT_TIME, waitInSecondsInt);
            }
        }
        return model;
    }

    protected async _toResource(activity: WaitActivity): Promise<Model.WaitActivity> {
        const modelWaitActivity: Model.WaitActivity = super._getCommonActivityProperties(activity, 'Wait') as Model.WaitActivity;

        modelWaitActivity.typeProperties = {
            waitTimeInSeconds: ValueAdapter.toResource(activity, WaitActivity.WAIT_TIME)
        };

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelWaitActivity, parsed);
        }

        return modelWaitActivity;
    }

    protected async _toModel(modelActivity: Model.WaitActivity): Promise<WaitActivity> {
        const activity: WaitActivity = WaitActivity.MODEL.create<WaitActivity>(modelActivity.name);
        this._updatedCommonProperties(modelActivity, activity);
        modelActivity.typeProperties = modelActivity.typeProperties || { waitTimeInSeconds: 1 };
        ValueAdapter.toModel(activity, WaitActivity.WAIT_TIME, modelActivity.typeProperties.waitTimeInSeconds);

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelActivity, WaitActivity.MODEL));
        return activity;
    }
}
